import { useSession } from '@grimme/next-grimme-auth';
import { jwtDecode } from 'jwt-decode';
import { useCallback } from 'react';

export const isJwtCurrentlyValid = (
  token?: string,
  skewSeconds = 15,
): boolean => {
  if (!token) return false;
  try {
    const payload: { exp?: number } = jwtDecode(token);

    if (!payload?.exp) return true; // if no exp, assume valid

    const nowSec = Math.floor(Date.now() / 1000) + skewSeconds;

    return payload.exp > nowSec;
  } catch {
    return false;
  }
};

/**
 * Composes the cache key `useAuthSWR` stores a query under: the query key
 * paired with the session token, or `null` without a valid session (nothing
 * is fetched or cached then). Shared with `useSWRQueryMutation().seed`, so
 * seeding hits the exact same entry.
 */
export const useAuthSWRKey = () => {
  const { data: sessionData } = useSession();
  const accessToken = sessionData?.accessToken;
  const isAuthenticated = !!accessToken && isJwtCurrentlyValid(accessToken);

  return useCallback(
    <TKey>(key: TKey | null | undefined): [TKey, string] | null =>
      isAuthenticated && key ? [key, accessToken as string] : null,
    [accessToken, isAuthenticated],
  );
};
